import {
  CurveFactory,
  Edge,
  EdgeRoutingMode,
  GeomEdge,
  GeomGraph,
  GeomNode,
  Graph,
  LayerDirectionEnum,
  Point as MsPoint,
  Node,
  SugiyamaLayoutSettings,
  layoutGeomGraph,
} from '@msagl/core'
import { produce } from 'immer'
import { difference as _difference } from 'lodash/fp'
import { capitalize as _capitalize } from 'lodash/fp'
import { v4 as uuidv4 } from 'uuid'

import {
  BoundaryEventRender,
  RenderableNode,
  RenderableSwimlane,
} from '@flowx/designer/modules/bpmn-editor/bpmn-canvas.types'
import {
  BOUNDARY_EVENT_SIZE,
  SWIMLANE_DEFAULT_LAYOUT_OPTIONS,
} from '@flowx/designer/modules/bpmn-editor/bpmn-graph.constants'
import {
  BpmnGraphUtil,
  getNodeDimensionsByType,
} from '@flowx/designer/modules/bpmn-editor/bpmn-graph.util'
import { Point, Rectangle } from '@flowx/designer/modules/bpmn-editor/dictionary/geometry.types'
import { getSequencePointsBetweenNodes } from '@flowx/designer/modules/bpmn-editor/sequence-routing.util'
import {
  NewNodeDefinition,
  NodeDefinition,
  NodeDefinitionNormalized,
  ProcessDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import {
  ShapeLayoutOptions,
  Swimlane,
  SwimlaneSummary,
} from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { configDefaults } from '@flowx/designer/modules/ui-designer/models/component-defaults'
import { NodePositionApi } from '@flowx/designer/shared/api/node.api'
import { ActionStores } from '@flowx/designer/shared/effector/action/action.stores'
import { AreasEvents } from '@flowx/designer/shared/effector/area/area.events'
import { AreaStores } from '@flowx/designer/shared/effector/area/area.stores'
import {
  BoundaryEventStore,
  BoundaryEventStores,
} from '@flowx/designer/shared/effector/boundary-event/boundary-event.stores'
import { NodeDefinitionStore, NodeStores } from '@flowx/designer/shared/effector/node/node.stores'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { SequenceStores } from '@flowx/designer/shared/effector/sequence/sequence.stores'
import {
  SwimlaneStores,
  SwimlaneSummaryStore,
} from '@flowx/designer/shared/effector/swimlane/swimlane.stores'
import { BoundaryEventTypeByNodeType } from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import { BoundaryEventEntity } from '@flowx/designer/shared/entities/boundary-event/boundary-event.entity'
import {
  BoundaryEvent,
  BoundaryEventNew,
  BoundaryEventPositionApi,
} from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { Area, NavigationType } from '@flowx/designer/shared/entities/nav-area.entity'
import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'
import { ActivityNode, EventNodes } from '@flowx/designer/shared/entities/node.types'
import {
  BoundaryEventsUpdates,
  NodeUpdates,
} from '@flowx/designer/shared/entities/process-api.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'
import { SequenceEntity } from '@flowx/designer/shared/entities/sequence.entity'
import { MathUtils } from '@flowx/designer/shared/utils/math.utils'

import {
  AiAnalystContextChanges,
  AiBoundaryEvent,
  AiBoundaryEventTypeToBoundaryEventMap,
  AiBoundaryEventWithNodeId,
  AiNode,
  AiNodeType,
  AiNodeTypeToNodeTypeMap,
  AiNodeWithSwimlaneId,
  AiProcess,
  AiProcessEntities,
  AiSwimlane,
  BoundaryEventTypeToAiBoundaryEventTypeMap,
  GenerateProcessResponse,
  NodeTypeToAiNodeTypeMap,
  PreviewAreaOutlinePadding,
  ProcessStateSnapshot,
  SelectedSequenceSnapshot,
} from '../../../types/ai.types'

type MsaglGraph = {
  graph: Graph
  geomGraph: GeomGraph
  msNodes: Map<string, Node>
  msEdges: Map<string, Edge>
  geometryNodes: Map<string, GeomNode>
  geometryEdges: Map<string, GeomEdge>
}

export class AiAnalystUtils {
  static calculateNodeIdsOffset(nodesIds: NodeDefinition['id'][]): number {
    const maxId = Math.max(...nodesIds)
    return MathUtils.getNextPowerOfTen(maxId)
  }

  static calculateSwimlaneIdsOffset(swimlanesIds: AiSwimlane['id'][]): number {
    const maxId = Math.max(...swimlanesIds)
    return MathUtils.getNextPowerOfTen(maxId)
  }

  static getProcessStateSnapshot(): ProcessStateSnapshot {
    return {
      process: ProcessEffector.processDefinition$.getState(),
      nodes: NodeStores.nodeDefinitions$.getState(),
      boundaryEvents: BoundaryEventStores.$boundaryEvents.getState(),
      sequences: SequenceStores.sequences$.getState(),
      swimlanes: SwimlaneStores.swimlaneSummaries$.getState(),
      actionDefinitions: ActionStores.$actionDefinitions.getState(),
    }
  }

  static getSelectedSequenceSnapshot(sequenceId: number): SelectedSequenceSnapshot {
    const selectedSequence = SequenceStores.sequences$.getState().get(sequenceId)
    const startNode = NodeStores.nodeDefinitions$.getState().get(selectedSequence.startNodeId)
    const endNode = NodeStores.nodeDefinitions$.getState().get(selectedSequence.endNodeId)

    const startSwimlane = SwimlaneStores.swimlaneSummaries$
      .getState()
      .get(startNode.swimlaneDefinitionId)
    const endSwimlane = SwimlaneStores.swimlaneSummaries$
      .getState()
      .get(endNode.swimlaneDefinitionId)

    return {
      selectedSequence,
      startNode,
      endNode,
      startSwimlane,
      endSwimlane,
    }
  }

  static getInitialProcessContext(): AiProcess | null {
    const processState = AiAnalystUtils.getProcessStateSnapshot()
    return AiAnalystUtils.mapProcessToAiProcessContext(processState).context
  }

  static getInitialProcessSequenceContext(
    processState: ProcessStateSnapshot,
    sequenceId: number
  ): {
    context: AiProcess
    swimlanesIdToUuid: Map<number, SwimlaneSummary['id']>
    swimlanesUuidToId: Map<SwimlaneSummary['id'], number>
    offset: number
    startNodeId: NodeDefinition['id']
    endNodeId: NodeDefinition['id']
    startSwimlaneUuid: SwimlaneSummary['id']
    endSwimlaneUuid: SwimlaneSummary['id']
  } {
    const selectedSequenceSnapshot = AiAnalystUtils.getSelectedSequenceSnapshot(sequenceId)

    const startNodeNormalized = processState.nodes.get(
      selectedSequenceSnapshot.selectedSequence.startNodeId
    )
    const endNodeNormalized = processState.nodes.get(
      selectedSequenceSnapshot.selectedSequence.endNodeId
    )

    const startNode = NodeEntity.denormalizeNode(startNodeNormalized, {
      boundaryEvents: processState.boundaryEvents,
      sequences: processState.sequences,
      actionDefinitions: processState.actionDefinitions,
    })
    const endNode = NodeEntity.denormalizeNode(endNodeNormalized, {
      boundaryEvents: processState.boundaryEvents,
      sequences: processState.sequences,
      actionDefinitions: processState.actionDefinitions,
    })

    const oldNodesIds = Array.from(processState.nodes.values()).map((node) => node.id)

    return {
      ...AiAnalystUtils.mapSequenceToAiInsertContext({
        startNode,
        endNode,
        startSwimlane: selectedSequenceSnapshot.startSwimlane,
        endSwimlane: selectedSequenceSnapshot.endSwimlane,
        swimlanes: processState.swimlanes,
      }),
      startNodeId: startNodeNormalized.id,
      endNodeId: endNodeNormalized.id,
      offset: AiAnalystUtils.calculateNodeIdsOffset(oldNodesIds),
      startSwimlaneUuid: selectedSequenceSnapshot.startSwimlane.id,
      endSwimlaneUuid: selectedSequenceSnapshot.endSwimlane.id,
    }
  }

  static getAiEntities(response: Pick<GenerateProcessResponse, 'answer'>): AiProcessEntities {
    const processStateSnapshot = AiAnalystUtils.getProcessStateSnapshot()
    return AiAnalystUtils.extractAiEntitiesFromResponse(response, processStateSnapshot)
  }

  static extractAiEntitiesFromResponse(
    response: Pick<GenerateProcessResponse, 'answer'>,
    processStateSnapshot: Omit<ProcessStateSnapshot, 'process'>
  ): AiProcessEntities {
    const { nodes, sequences, swimlanes, boundaryEvents } = processStateSnapshot
    const { answer: aiProcess } = response

    return AiAnalystUtils.mapAiProcessContextToProcessEntities({
      aiProcess,
      oldNodes: nodes,
      oldSequences: sequences,
      oldBoundaryEvents: boundaryEvents,
      oldSwimlanes: swimlanes,
      swimlanesIdToUuid: AiAnalystUtils.mapSwimlanesUuidToId(swimlanes).swimlanesIdToUuid,
    })
  }

  static getProcessChanges(
    aiEntities: AiProcessEntities,
    nodes: NodeDefinitionStore,
    boundaryEvents: BoundaryEventStore
  ): AiAnalystContextChanges {
    const addedNodes = Object.entries(aiEntities.updates.nodes.added).map(([id, node]) => ({
      id,
      name: node.name,
      type: node.type,
    }))

    const addedBoundaries = Object.entries(aiEntities.updates.boundaryEvents.added).map(
      ([id, boundary]) => ({
        id,
        name: boundary.name,
        type: boundary.type,
      })
    )

    const removedNodes = aiEntities.updates.nodes.deleted.map((id) => {
      const deletedNode = nodes.get(id)
      return {
        id,
        name: deletedNode.name,
        type: deletedNode.type,
      }
    })

    const removedBoundaries = aiEntities.updates.boundaryEvents.deleted.map((id) => {
      const deletedBoundary = boundaryEvents.get(id)
      return {
        id,
        name: deletedBoundary.name,
        type: deletedBoundary.type,
      }
    })

    return {
      added: [...addedNodes, ...addedBoundaries],
      removed: [...removedNodes, ...removedBoundaries],
    }
  }

  static mapProcessToAiProcessContext(payload: {
    swimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary>
    process: ProcessDefinition
    nodes: Map<RenderableNode['id'], RenderableNode>
    sequences: Map<SequencePoints['id'], SequencePoints>
    boundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
  }): { context: AiProcess; swimlanesIdToUuid: Map<number, SwimlaneSummary['id']> } {
    const { process, nodes, sequences, boundaryEvents, swimlanes } = payload
    const nodesArray = [...nodes.values()]
    const swimlanesArray = [...payload.swimlanes.values()]

    const { swimlanesUuidToId, swimlanesIdToUuid } = this.mapSwimlanesUuidToId(swimlanes)

    return {
      swimlanesIdToUuid,
      context: {
        pt: process.resourceName,
        pd: '',
        md: '',
        sl: swimlanesArray.map((swimlane) => ({
          id: swimlanesUuidToId.get(swimlane.id),
          sn: swimlane.name,
          nl: nodesArray
            .filter((node) => node.swimlaneDefinitionId === swimlane.id)
            .map((node) => AiAnalystUtils.mapNodeToAiNode({ node, sequences, boundaryEvents })),
        })),
      },
    }
  }

  static mapSwimlanesUuidToId(swimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary>): {
    swimlanesUuidToId: Map<SwimlaneSummary['id'], number>
    swimlanesIdToUuid: Map<number, SwimlaneSummary['id']>
  } {
    const swimlanesUuidToId = new Map()
    const swimlanesIdToUuid = new Map()

    Array.from(swimlanes.values()).forEach((swimlane, index) => {
      const uuid = swimlane.id
      swimlanesUuidToId.set(uuid, index)
      swimlanesIdToUuid.set(index, uuid)
    })

    return {
      swimlanesUuidToId,
      swimlanesIdToUuid,
    }
  }

  static mapSequenceToAiInsertContext(payload: {
    startNode: NodeDefinition
    endNode: NodeDefinition
    startSwimlane: SwimlaneSummary
    endSwimlane: SwimlaneSummary
    swimlanes: SwimlaneSummaryStore
  }): {
    context: AiProcess
    swimlanesIdToUuid: Map<number, SwimlaneSummary['id']>
    swimlanesUuidToId: Map<SwimlaneSummary['id'], number>
  } {
    const { startNode, endNode, startSwimlane, endSwimlane, swimlanes } = payload

    const { swimlanesIdToUuid, swimlanesUuidToId } = AiAnalystUtils.mapSwimlanesUuidToId(swimlanes)

    const startNodeAi = {
      nt: AiAnalystUtils.mapNodeTypeToAiNodeType(startNode.type),
      id: startNode.id,
      lb: startNode.name,
      events: startNode.messageEvents.map(AiAnalystUtils.mapBoundaryEventToAiBoundaryEvent),
      ins: startNode.incomingSequences.map((sequence) => sequence.startNodeId),
      ous: [endNode.id],
    }

    const endNodeAi = {
      nt: AiAnalystUtils.mapNodeTypeToAiNodeType(endNode.type),
      id: endNode.id,
      lb: endNode.name,
      events: endNode.messageEvents.map(AiAnalystUtils.mapBoundaryEventToAiBoundaryEvent),
      ins: [startNode.id],
      ous: endNode.outgoingSequences.map((sequence) => sequence.endNodeId),
    }

    const sameSwimlane = startSwimlane.id === endSwimlane.id
    const startEndNodesAiSwimlanes = sameSwimlane
      ? [
          {
            id: swimlanesUuidToId.get(startSwimlane.id),
            sn: startSwimlane.name,
            nl: [startNodeAi, endNodeAi],
          },
        ]
      : [
          {
            id: swimlanesUuidToId.get(startSwimlane.id),
            sn: startSwimlane.name,
            nl: [startNodeAi],
          },
          {
            id: swimlanesUuidToId.get(endSwimlane.id),
            sn: endSwimlane.name,
            nl: [endNodeAi],
          },
        ]

    return {
      context: {
        pt: '',
        pd: '',
        md: '',
        sl: startEndNodesAiSwimlanes,
      },
      swimlanesIdToUuid,
      swimlanesUuidToId,
    }
  }

  static mergeUpdatesWithExistingProcess(
    oldProcess: {
      process: ProcessDefinition
      swimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary>
      nodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
      sequences: Map<SequencePoints['id'], SequencePoints>
      boundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
    },
    updates: {
      aiProcess: AiProcess
      startNodeId: NodeDefinition['id']
      endNodeId: NodeDefinition['id']
      startNodeSwimlaneUuid: SwimlaneSummary['id']
      endNodeSwimlaneUuid: SwimlaneSummary['id']
      swimlanesUuidToId: Map<SwimlaneSummary['id'], number>
    }
  ): AiProcessEntities {
    const {
      aiProcess,
      startNodeId,
      endNodeId,
      startNodeSwimlaneUuid,
      endNodeSwimlaneUuid,
      swimlanesUuidToId,
    } = updates

    const { nodes, sequences, swimlanes, boundaryEvents } = oldProcess

    const startSwimlaneId = swimlanesUuidToId.get(startNodeSwimlaneUuid)
    const endSwimlaneId = swimlanesUuidToId.get(endNodeSwimlaneUuid)

    const updatedSwimlanes = new Map([
      [startNodeSwimlaneUuid, aiProcess.sl.find((aiSwimlane) => aiSwimlane.id === startSwimlaneId)],
      [endNodeSwimlaneUuid, aiProcess.sl.find((aiSwimlane) => aiSwimlane.id === endSwimlaneId)],
    ])

    // Map the existing process to the AI Process format so that we can merge the updates with the existing process
    const { context: existingAiProcess, swimlanesIdToUuid: existingSwimlanesIdToUuid } =
      AiAnalystUtils.mapProcessToAiProcessContext(oldProcess)

    // Calculate the value we should add to the new swimlanes ids so that they don't conflict with the existing swimlanes
    const newSwimlanesIdOffset = AiAnalystUtils.calculateSwimlaneIdsOffset(
      existingAiProcess.sl.map((swimlane) => swimlane.id)
    )

    // Find new swimlanes and pad their id so that they don't conflict with the existing swimlanes
    const newSwimlanes = aiProcess.sl
      .filter((aiSwimlane) => ![startSwimlaneId, endSwimlaneId].includes(aiSwimlane.id))
      .map((aiSwimlane) =>
        produce(aiSwimlane, (draft) => {
          draft.id = draft.id + newSwimlanesIdOffset
        })
      )

    const allExistingProcessNodes = existingAiProcess.sl.flatMap((swimlane) => swimlane.nl)
    const existingEndNode = allExistingProcessNodes.find((node) => node.id === endNodeId)
    const existingStartNode = allExistingProcessNodes.find((node) => node.id === startNodeId)

    const allUpdatedProcessNodes = aiProcess.sl.flatMap((swimlane) => swimlane.nl)
    const generatedEndNode = allUpdatedProcessNodes.find((node) => node.id === endNodeId)
    const generatedStartNode = allUpdatedProcessNodes.find((node) => node.id === startNodeId)

    const mergedProcess = produce(existingAiProcess, (draft) => {
      draft.sl.forEach((existingSwimlane) => {
        const existingSwimlaneUuid = existingSwimlanesIdToUuid.get(existingSwimlane.id)

        if (!updatedSwimlanes.has(existingSwimlaneUuid)) {
          return
        }

        const updatedSwimlaneNodes = updatedSwimlanes.get(existingSwimlaneUuid).nl

        const newNodes = updatedSwimlanes
          .get(existingSwimlaneUuid)
          .nl.filter(
            (newNode) => !existingSwimlane.nl.find((existingNode) => existingNode.id === newNode.id)
          )

        const swimlaneContainsStartNode = !!updatedSwimlaneNodes.find(
          (node) => node.id === startNodeId
        )

        const startNodeUpdated = swimlaneContainsStartNode
          ? produce(existingStartNode, (draft) => {
              const changedOutgoingSequences = _difference(
                generatedStartNode.ous,
                existingStartNode.ous
              )

              draft.ous = existingStartNode.ous.map((outgoingSequenceId) => {
                if (outgoingSequenceId === endNodeId) {
                  return changedOutgoingSequences[0]
                } else {
                  return outgoingSequenceId
                }
              })

              const events = draft.events || []

              events.forEach((event) => {
                // 1. Check if one of the events has the ous id the endNode id
                // 2. If it does, replace the ous id with the generated end node id
                if (event.ous.includes(endNodeId)) {
                  event.ous = changedOutgoingSequences
                }
              })
            })
          : null

        const swimlaneContainsEndNode = !!updatedSwimlaneNodes.find((node) => node.id === endNodeId)

        const endNodeUpdated = swimlaneContainsEndNode
          ? produce(existingEndNode, (draft) => {
              const changedIncomingSequences = _difference(
                generatedEndNode.ins,
                existingEndNode.ins
              )

              draft.ins = existingEndNode.ins.map((incomingSequenceId) => {
                if (incomingSequenceId === startNodeId) {
                  return changedIncomingSequences[0]
                } else {
                  return incomingSequenceId
                }
              })
            })
          : null

        const existingNodesWithoutStartAndEnd = existingSwimlane.nl.filter(
          (node) => node.id !== startNodeId && node.id !== endNodeId
        )

        existingSwimlane.nl = [
          ...existingNodesWithoutStartAndEnd,
          ...newNodes,
          ...(startNodeUpdated ? [startNodeUpdated] : []),
          ...(endNodeUpdated ? [endNodeUpdated] : []),
        ]
      })

      draft.sl.push(...newSwimlanes)
    })

    return AiAnalystUtils.mapAiProcessContextToProcessEntities({
      aiProcess: mergedProcess,
      oldNodes: nodes,
      oldSequences: sequences,
      oldBoundaryEvents: boundaryEvents,
      oldSwimlanes: swimlanes,
      swimlanesIdToUuid: existingSwimlanesIdToUuid,
    })
  }

  static mapAiProcessContextToProcessEntities(payload: {
    aiProcess: AiProcess
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
    oldSequences: Map<SequencePoints['id'], SequencePoints>
    oldBoundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
    oldSwimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary>
    swimlanesIdToUuid: Map<number, SwimlaneSummary['id']>
  }): AiProcessEntities {
    const {
      aiProcess,
      oldNodes,
      oldSequences,
      oldSwimlanes,
      oldBoundaryEvents,
      swimlanesIdToUuid,
    } = payload

    const newSwimlanesIdToUuid = new Map<number, SwimlaneSummary['id']>()
    const aiSwimlanes = aiProcess.sl.map((aiSwimlane) => {
      const existingUuid = swimlanesIdToUuid.get(aiSwimlane.id)

      const swimlaneWithMatchingName = Array.from(oldSwimlanes.values()).find(
        (swimlane) => swimlane.name === aiSwimlane.sn
      )

      if (!existingUuid && swimlaneWithMatchingName) {
        newSwimlanesIdToUuid.set(aiSwimlane.id, swimlaneWithMatchingName.id)
      }

      if (!existingUuid && !swimlaneWithMatchingName) {
        newSwimlanesIdToUuid.set(aiSwimlane.id, uuidv4())
      }

      return {
        ...aiSwimlane,
        id: swimlanesIdToUuid.get(aiSwimlane.id) ?? newSwimlanesIdToUuid.get(aiSwimlane.id),
      }
    })

    const aiNodes: AiNodeWithSwimlaneId[] = aiSwimlanes.flatMap((aiSwimlane) =>
      aiSwimlane.nl.map((aiNode) => ({ ...aiNode, swimlaneId: aiSwimlane.id }))
    )

    const aiBoundaryEvents: AiBoundaryEventWithNodeId[] = aiNodes.flatMap((aiNode) =>
      (aiNode.events || []).map((aiEvent) => ({ ...aiEvent, nodeId: aiNode.id }))
    )

    const nodes: (RenderableNode & NewNodeDefinition)[] = aiNodes.map((aiNode) => ({
      id: aiNode.id,
      type: AiNodeTypeToNodeTypeMap[aiNode.nt],
      name: aiNode.lb,
      incomingSequences: aiNode.ins,
      outgoingSequences: aiNode.ous,
      swimlaneDefinitionId: aiNode.swimlaneId,
      canGoBack: false,
      messageEvents: [],
      timerEvents: [],
      errorEvents: [],
      layoutOptions: null,
    }))

    const boundaryEvents: Omit<BoundaryEvent, 'flowxUuid'>[] = aiBoundaryEvents.map(
      (aiBoundaryEvent) => {
        const boundaryEventType = AiBoundaryEventTypeToBoundaryEventMap[aiBoundaryEvent.et]
        if (boundaryEventType == null) {
          throw new Error(
            `Unknown boundary event type "${aiBoundaryEvent.et}". Expected one of: ${Object.keys(AiBoundaryEventTypeToBoundaryEventMap).join(', ')}`
          )
        }

        return {
          ...BoundaryEventEntity.createBoundaryEventFromBoundaryEventType(
            {
              name: aiBoundaryEvent.name,
              type: boundaryEventType,
              layoutOptions: null,
            },
            nodes.find((node) => node.id === aiBoundaryEvent.nodeId)
          ),
          id: aiBoundaryEvent.id,
        }
      }
    )

    const swimlanes: RenderableSwimlane[] = aiSwimlanes.map((aiSwimlane) => ({
      id: aiSwimlane.id,
      name: aiSwimlane.sn,
      layoutOptions: null,
    }))

    const nodesGroupedBySwimlane = aiSwimlanes.reduce(
      (acc, aiSwimlane) =>
        acc.set(
          aiSwimlane.id,
          aiSwimlane.nl.map((aiNode) => aiNode.id)
        ),
      new Map<SwimlaneSummary['id'], AiNode['id'][]>()
    )

    const sequences = AiAnalystUtils.getSequencesFromAiNodes(aiNodes)

    const layout = AiAnalystUtils.layoutProcess({
      nodes,
      boundaryEvents,
      sequences,
      nodesGroupedBySwimlane,
    })

    const positionedNodes = nodes.map((node) =>
      produce(node, (draft) => {
        draft.layoutOptions = layout.nodes.get(node.id)
      })
    )

    const positionedBoundaryEvents = boundaryEvents.map((boundaryEvent) =>
      produce(boundaryEvent, (draft) => {
        draft.layoutOptions = layout.boundaryEvents.get(boundaryEvent.id)
      })
    )

    const positionedSequences = sequences.map(
      ({ startNodeId, endNodeId, boundaryEventId }, index) => ({
        id: index,
        startNodeId,
        endNodeId,
        boundaryEventId,
        layoutOptions: layout.sequences.get(`${startNodeId}->${endNodeId}`),
      })
    )

    const positionedSwimlanes = swimlanes.map((swimlane) =>
      produce(swimlane, (draft) => {
        draft.layoutOptions = layout.swimlanes.get(swimlane.id)
      })
    )

    const nodesUpdates = AiAnalystUtils.getNodeUpdates({
      oldNodes,
      aiNodes: positionedNodes,
    })

    const sequencesUpdates = AiAnalystUtils.getSequenceUpdates({
      oldBoundaryEvents,
      oldSequences,
      oldNodes,
      aiSequences: positionedSequences,
    })

    const swimlanesUpdates = AiAnalystUtils.getSwimlaneUpdates({
      oldSwimlanes,
      aiSwimlanes: positionedSwimlanes,
    })

    const boundaryEventsUpdates = AiAnalystUtils.getBoundaryEventUpdates({
      oldNodes,
      oldBoundaryEvents,
      aiBoundaryEvents: positionedBoundaryEvents,
    })

    const previewOutline = AiAnalystUtils.calculatePreviewAreaOutline({
      positionedSwimlanes,
      nodesUpdates,
      oldNodes,
      addedBoundaries: boundaryEventsUpdates.added,
    })

    return {
      nodes: positionedNodes,
      boundaryEvents: positionedBoundaryEvents,
      sequences: positionedSequences as SequencePoints[],
      swimlanes: positionedSwimlanes,
      updates: {
        nodes: AiAnalystUtils.mapAiNodeUpdatesToApiPayload(nodesUpdates),
        sequences: sequencesUpdates,
        swimlanes: swimlanesUpdates,
        boundaryEvents: AiAnalystUtils.mapAiBoundaryEventUpdatesToApiPayload(boundaryEventsUpdates),
      },
      previewOutline,
    }
  }

  static calculatePreviewAreaOutline(payload: {
    positionedSwimlanes: RenderableSwimlane[]
    nodesUpdates: {
      added: (NewNodeDefinition & RenderableNode)[]
      updated: NodePositionApi[]
      deleted: NodeDefinition['id'][]
    }
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
    addedBoundaries: BoundaryEventNew[]
  }): ShapeLayoutOptions {
    const { positionedSwimlanes, nodesUpdates, oldNodes, addedBoundaries } = payload
    const swimlanesMap = new Map(positionedSwimlanes.map((swimlane) => [swimlane.id, swimlane]))
    const addedNodesWithAbsolutePosition = nodesUpdates.added.map((node) => {
      const swimlane = swimlanesMap.get(node.swimlaneDefinitionId)
      const layoutOptions = BpmnGraphUtil.getNodeAbsolutePosition(swimlane, node)

      return {
        ...node,
        layoutOptions,
      }
    })

    const addedBoundariesWithAbsolutePosition = addedBoundaries.map((boundaryEvent) => {
      const addedNode = addedNodesWithAbsolutePosition.find(
        (node) => node.id === boundaryEvent.nodeDefinitionId
      )

      const updatedNode = nodesUpdates.updated.find(
        (node) => node.id === boundaryEvent.nodeDefinitionId
      )

      const oldNode = oldNodes.get(boundaryEvent.nodeDefinitionId)

      const node = addedNode ?? updatedNode ?? oldNode

      const layoutOptions = BpmnGraphUtil.getBoundaryEventAbsolutePosition(node, boundaryEvent)
      return {
        ...boundaryEvent,
        layoutOptions,
      }
    })

    const shapeLayoutOptions = [
      ...addedNodesWithAbsolutePosition.map((node) => ({
        layoutOptions: {
          x: node.layoutOptions.x,
          y: node.layoutOptions.y,
          width: getNodeDimensionsByType(node.type).width,
          height: getNodeDimensionsByType(node.type).height,
        },
      })),
      ...addedBoundariesWithAbsolutePosition.map((boundary) => ({
        layoutOptions: {
          x: boundary.layoutOptions.x,
          y: boundary.layoutOptions.y,
          width: BOUNDARY_EVENT_SIZE,
          height: BOUNDARY_EVENT_SIZE,
        },
      })),
    ]

    const previewAreaEmpty = !shapeLayoutOptions.length
    const rectanglesBoundingBox = BpmnGraphUtil.rectangleBoundingBoxFromLayoutOptions(
      !previewAreaEmpty
        ? shapeLayoutOptions
        : positionedSwimlanes.map(({ layoutOptions }) => ({ layoutOptions }))
    )

    const paddedBoundingBox = !previewAreaEmpty
      ? BpmnGraphUtil.addPaddingToRect(rectanglesBoundingBox, PreviewAreaOutlinePadding)
      : BpmnGraphUtil.removePaddingFromRect(rectanglesBoundingBox, PreviewAreaOutlinePadding)

    return {
      x: paddedBoundingBox.tl.x,
      y: paddedBoundingBox.tl.y,
      width: paddedBoundingBox.br.x - paddedBoundingBox.bl.x,
      height: paddedBoundingBox.br.y - paddedBoundingBox.tr.y,
    }
  }

  static mapNodeToAiNode(payload: {
    node: RenderableNode
    sequences: Map<SequencePoints['id'], SequencePoints>
    boundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
  }): AiNode {
    const { node, sequences } = payload

    const incomingNodesIds = node.incomingSequences.map(
      (sequenceId) => sequences.get(sequenceId).startNodeId
    )

    const outgoingNodesIds = node.outgoingSequences.map(
      (sequenceId) => sequences.get(sequenceId).endNodeId
    )

    const eventsIds = [...node.messageEvents, ...node.timerEvents, ...node.errorEvents]

    const events = !NodeEntity.isEventNode(node)
      ? eventsIds.map((eventId) => {
          const boundary = payload.boundaryEvents.get(eventId)
          return {
            id: boundary.id,
            name: boundary.name,
            et: BoundaryEventTypeToAiBoundaryEventTypeMap[boundary.type],
            ous: node.outgoingSequences
              .filter((sequenceId) => sequences.get(sequenceId).boundaryEventId === eventId)
              .map((sequenceId) => sequences.get(sequenceId).endNodeId),
          }
        })
      : []

    return {
      nt: AiAnalystUtils.mapNodeTypeToAiNodeType(node.type),
      id: node.id,
      lb: node.name,
      events,
      ins: incomingNodesIds,
      ous: outgoingNodesIds,
    }
  }

  static mapNodeTypeToAiNodeType(nodeType: NodeDefinition['type']): AiNodeType {
    return NodeTypeToAiNodeTypeMap[nodeType]
  }

  static mapBoundaryEventToAiBoundaryEvent(boundaryEvent: BoundaryEvent): AiBoundaryEvent {
    return {
      id: boundaryEvent.id,
      name: boundaryEvent.name,
      et: BoundaryEventTypeToAiBoundaryEventTypeMap[boundaryEvent.type],
      ous: [boundaryEvent.outgoingSequence.endNodeId],
    }
  }

  static layoutProcess(payload: {
    nodes: RenderableNode[]
    boundaryEvents: Omit<BoundaryEvent, 'flowxUuid'>[]
    sequences: {
      startNodeId: NodeDefinition['id']
      endNodeId: NodeDefinition['id']
      boundaryEventId: BoundaryEvent['id']
    }[]
    nodesGroupedBySwimlane: Map<SwimlaneSummary['id'], AiNode['id'][]>
  }): {
    graph: {
      width: number
      height: number
    }
    nodes: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>
    boundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent['layoutOptions']>
    sequences: Map<string, SequencePoints['layoutOptions']>
    swimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>
  } {
    const { nodes, boundaryEvents, sequences, nodesGroupedBySwimlane } = payload
    const msaglGraph = AiAnalystUtils.#createLayoutedMsaglGraph(nodes, sequences)
    const nodesMap = AiAnalystUtils.#extractNodesPositionsFromMsaglGraph(msaglGraph)
    const sequencesMap = AiAnalystUtils.#extractSequencesPositionsFromMsaglGraph(msaglGraph)

    // SWIMLANES
    const boundingBoxes = AiAnalystUtils.#calculateNodesBoundingBoxesBySwimlane(
      nodesGroupedBySwimlane,
      nodesMap,
      nodes
    )

    const nodesYCoordsRelativeToSwimlanes = this.#calculateNodesYCoordRelativeToTheirParentSwimlane(
      nodesGroupedBySwimlane,
      nodesMap,
      boundingBoxes
    )

    const boundaryEventsPositions = AiAnalystUtils.#calculateBoundaryEventsPositions(
      nodes,
      boundaryEvents
    )

    const updatedSequencesPositions =
      AiAnalystUtils.#calculateUpdatedSequencesPositionsForNormalizedNodesPositions(
        sequences,
        nodesYCoordsRelativeToSwimlanes,
        sequencesMap,
        nodesMap
      )

    const PADDING = {
      x: 300,
      y: 300,
    }

    const swimlanesPositions = AiAnalystUtils.#initializeSwimlanesLayout(boundingBoxes, PADDING)

    const swimlanesDeltaY = AiAnalystUtils.#calculateSwimlanesYDelta(swimlanesPositions)

    const swimlanesMovedMap = AiAnalystUtils.#moveSwimlanesWithDeltaY(
      swimlanesPositions,
      swimlanesDeltaY
    )

    const nodesMapMovedByPadding = AiAnalystUtils.#moveNodesForSwimlanePadding(
      nodesYCoordsRelativeToSwimlanes,
      PADDING
    )

    const sequencesMapMoved = AiAnalystUtils.#fitSequencesToSwimlanesMovement(
      nodes,
      PADDING,
      updatedSequencesPositions,
      swimlanesDeltaY,
      swimlanesMovedMap,
      nodesMapMovedByPadding,
      sequences
    )

    const sequencesAdjustedToBoundaryEvents = sequences.reduce((acc, sequence) => {
      const sequenceId = `${sequence.startNodeId}->${sequence.endNodeId}`
      if (sequence.boundaryEventId) {
        const startNode = nodes.find((node) => node.id === sequence.startNodeId)
        const endNode = nodes.find((node) => node.id === sequence.endNodeId)

        const startNodeSwimlaneId = startNode.swimlaneDefinitionId
        const endNodeSwimlaneId = endNode.swimlaneDefinitionId

        const startSwimlane = {
          layoutOptions: swimlanesMovedMap.get(startNodeSwimlaneId),
        }

        const endSwimlane = {
          layoutOptions: swimlanesMovedMap.get(endNodeSwimlaneId),
        }

        const startNodeWithAbsolutePosition = produce(startNode, (draft) => {
          draft.layoutOptions = BpmnGraphUtil.getNodeAbsolutePosition(startSwimlane, {
            layoutOptions: nodesMapMovedByPadding.get(sequence.startNodeId),
          })
        })

        const startBoundaryWithAbsolutePosition = {
          type: boundaryEvents.find(
            (boundaryEvent) => boundaryEvent.id === sequence.boundaryEventId
          ).type,

          layoutOptions: BpmnGraphUtil.add(
            startNodeWithAbsolutePosition.layoutOptions,
            boundaryEventsPositions.get(sequence.boundaryEventId)
          ),
        }

        const endNodeWithAbsolutePosition = produce(endNode, (draft) => {
          draft.layoutOptions = BpmnGraphUtil.getNodeAbsolutePosition(endSwimlane, {
            layoutOptions: nodesMapMovedByPadding.get(sequence.endNodeId),
          })
        })

        return produce(acc, (draft) => {
          const newLayoutOptions = getSequencePointsBetweenNodes(
            startBoundaryWithAbsolutePosition,
            endNodeWithAbsolutePosition
          )
          // TODO: See how to fix WriableDraft typing problem
          draft.set(sequenceId, newLayoutOptions as unknown as any)
        })
      } else {
        return produce(acc, (draft) => {
          draft.set(sequenceId, sequencesMapMoved.get(sequenceId))
        })
      }
    }, new Map<string, SequencePoints['layoutOptions']>())

    return {
      graph: {
        width: msaglGraph.geomGraph.boundingBox.width,
        height: msaglGraph.geomGraph.boundingBox.height,
      },
      boundaryEvents: boundaryEventsPositions,
      nodes: nodesMapMovedByPadding,
      sequences: sequencesAdjustedToBoundaryEvents,
      swimlanes: swimlanesMovedMap,
    }
  }

  static getNodeUpdates(payload: {
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
    aiNodes: (NewNodeDefinition & RenderableNode)[]
  }): {
    added: (NewNodeDefinition & RenderableNode)[]
    updated: NodePositionApi[]
    deleted: NodeDefinition['id'][]
  } {
    const { oldNodes, aiNodes } = payload

    const added = aiNodes.filter((aiNode) => !oldNodes.has(aiNode.id))

    const deleted = [...oldNodes.values()]
      .map((node) => node.id)
      .filter((id) => !aiNodes.find((aiNode) => aiNode.id === id))

    const updated = aiNodes
      .filter((aiNode) => oldNodes.has(aiNode.id))
      .map((aiNode) => ({
        id: aiNode.id,
        layoutOptions: aiNode.layoutOptions,
        swimlaneDefinitionId: aiNode.swimlaneDefinitionId,
        isNavAreaAssigned: false,
      }))

    return {
      added,
      updated,
      deleted,
    }
  }

  static mapAiNodeUpdatesToApiPayload(nodeUpdates: {
    added: (NewNodeDefinition & RenderableNode)[]
    updated: NodePositionApi[]
    deleted: NodeDefinition['id'][]
  }): NodeUpdates {
    return {
      added: nodeUpdates.added.reduce(
        (acc, aiNode) => {
          const { id, ...nodeWithoutId } = aiNode
          return {
            ...acc,
            [AiAnalystUtils.mapNewNodeAiIdToEndpointId(aiNode.id)]: nodeWithoutId,
          }
        },
        {} as Record<string, NewNodeDefinition>
      ),
      updated: nodeUpdates.updated,
      deleted: nodeUpdates.deleted,
    }
  }

  static mapAiBoundaryEventUpdatesToApiPayload(boundaryEventUpdates: {
    added: (BoundaryEventNew & { id: BoundaryEvent['id']; nodeFlowxUuid: string })[]
    updated: BoundaryEventPositionApi[]
    deleted: BoundaryEvent['id'][]
  }): BoundaryEventsUpdates {
    return {
      added: boundaryEventUpdates.added.reduce(
        (acc, aiBoundaryEvent) => ({
          ...acc,
          [AiAnalystUtils.mapNewBoundaryEventAiIdToEndpointId(aiBoundaryEvent.id)]: aiBoundaryEvent,
        }),
        {}
      ),
      updated: boundaryEventUpdates.updated,
      deleted: boundaryEventUpdates.deleted,
    }
  }

  static getSequenceUpdates(payload: {
    oldSequences: Map<SequencePoints['id'], SequencePoints>
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
    oldBoundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
    aiSequences: Pick<
      SequencePoints,
      'id' | 'layoutOptions' | 'startNodeId' | 'endNodeId' | 'boundaryEventId'
    >[]
  }): {
    added: {
      startNodeFlowxUuid: string
      endNodeFlowxUuid: string
      boundaryEventFlowxUuid: string
      layoutOptions: SequencePoints['layoutOptions']
    }[]
    updated: Pick<SequencePoints, 'id' | 'layoutOptions'>[]
    deleted: number[]
  } {
    const { aiSequences, oldSequences, oldNodes, oldBoundaryEvents } = payload
    const existingSequencesArray = [...oldSequences.values()]

    const added = aiSequences.filter(
      (aiSequence) =>
        !existingSequencesArray.find(
          (sequence) =>
            sequence.startNodeId === aiSequence.startNodeId &&
            sequence.endNodeId === aiSequence.endNodeId
        )
    )

    const addedWithStringIds = added.map(
      ({ startNodeId, endNodeId, boundaryEventId, layoutOptions }) => ({
        startNodeFlowxUuid:
          oldNodes.get(startNodeId)?.flowxUuid ??
          AiAnalystUtils.mapNewNodeAiIdToEndpointId(startNodeId),
        endNodeFlowxUuid:
          oldNodes.get(endNodeId)?.flowxUuid ??
          AiAnalystUtils.mapNewNodeAiIdToEndpointId(endNodeId),
        layoutOptions,
        boundaryEventFlowxUuid: boundaryEventId
          ? (oldBoundaryEvents.get(boundaryEventId)?.flowxUuid ??
            AiAnalystUtils.mapNewBoundaryEventAiIdToEndpointId(boundaryEventId))
          : null,
      })
    )

    const deleted = existingSequencesArray
      .filter(
        (sequence) =>
          !aiSequences.find(
            (aiSequence) =>
              sequence.startNodeId === aiSequence.startNodeId &&
              sequence.endNodeId === aiSequence.endNodeId
          )
      )
      .map((sequence) => sequence.id)

    const updated = aiSequences
      .map((sequence) => {
        const existingSequence = existingSequencesArray.find(
          (existingSequence) =>
            existingSequence.startNodeId === sequence.startNodeId &&
            existingSequence.endNodeId === sequence.endNodeId
        )

        return {
          id: existingSequence?.id ?? null,
          layoutOptions: sequence.layoutOptions,
        }
      })
      .filter((sequence) => sequence.id)

    return {
      added: addedWithStringIds,
      updated,
      deleted,
    }
  }

  static getSwimlaneUpdates(payload: {
    oldSwimlanes: Map<SwimlaneSummary['id'], SwimlaneSummary>
    aiSwimlanes: RenderableSwimlane[]
  }): {
    added: Record<
      SwimlaneSummary['id'],
      Pick<Swimlane, 'name' | 'layoutOptions' | 'operationRoles' | 'allowMultipleExecutors'>
    >
    updated: Pick<SwimlaneSummary, 'id' | 'layoutOptions' | 'name'>[]
    deleted: SwimlaneSummary['id'][]
  } {
    const { oldSwimlanes, aiSwimlanes } = payload

    const added = aiSwimlanes
      .filter((aiSwimlane) => !oldSwimlanes.has(aiSwimlane.id))
      .reduce(
        (acc, aiSwimlane) => ({
          ...acc,
          [aiSwimlane.id]: {
            name: aiSwimlane.name,
            layoutOptions: aiSwimlane.layoutOptions,
            operationRoles: {},
            allowMultipleExecutors: true,
          },
        }),
        {}
      )

    const updated = aiSwimlanes
      .filter((aiSwimlane) => oldSwimlanes.has(aiSwimlane.id))
      .map((aiSwimlane) => ({
        id: aiSwimlane.id,
        name: aiSwimlane.name,
        layoutOptions: aiSwimlane.layoutOptions,
      }))

    const deleted = [...oldSwimlanes.keys()].filter(
      (id) => !aiSwimlanes.find((aiSwimlane) => aiSwimlane.id === id)
    )

    return {
      added,
      updated,
      deleted,
    }
  }

  static getBoundaryEventUpdates(payload: {
    oldNodes: Map<NodeDefinition['id'], NodeDefinitionNormalized>
    oldBoundaryEvents: Map<BoundaryEvent['id'], BoundaryEvent>
    aiBoundaryEvents: Omit<BoundaryEvent, 'flowxUuid'>[]
  }): {
    added: (BoundaryEventNew & {
      id: BoundaryEvent['id']
      nodeFlowxUuid: string
    })[]
    updated: BoundaryEventPositionApi[]
    deleted: BoundaryEvent['id'][]
  } {
    const { oldNodes, oldBoundaryEvents, aiBoundaryEvents } = payload

    const added = aiBoundaryEvents.filter(
      (aiBoundaryEvent) => !oldBoundaryEvents.has(aiBoundaryEvent.id)
    )

    const addedWithParentFlowxUuid = added.map((aiBoundaryEvent) => ({
      ...aiBoundaryEvent,
      nodeFlowxUuid:
        oldNodes.get(aiBoundaryEvent.nodeDefinitionId)?.flowxUuid ??
        AiAnalystUtils.mapNewNodeAiIdToEndpointId(aiBoundaryEvent.nodeDefinitionId),
    }))

    const deleted = [...oldBoundaryEvents.values()]
      .filter((boundaryEvent) => {
        const parentNode = oldNodes.get(boundaryEvent.nodeDefinitionId)

        return BoundaryEventTypeByNodeType[parentNode.type] !== boundaryEvent.type
      })
      .map((boundaryEvent) => boundaryEvent.id)
      .filter((id) => !aiBoundaryEvents.find((aiBoundaryEvent) => aiBoundaryEvent.id === id))

    const updated = aiBoundaryEvents
      .filter((aiBoundaryEvent) => oldBoundaryEvents.has(aiBoundaryEvent.id))
      .map((aiBoundaryEvent) => ({
        id: aiBoundaryEvent.id,
        layoutOptions: aiBoundaryEvent.layoutOptions,
        type: aiBoundaryEvent.type,
      }))

    return {
      added: addedWithParentFlowxUuid,
      updated,
      deleted,
    }
  }

  static getSequencesFromAiNodes(aiNodes: AiNode[]): {
    startNodeId: SequencePoints['startNodeId']
    endNodeId: SequencePoints['endNodeId']
    boundaryEventId: SequencePoints['boundaryEventId']
  }[] {
    return aiNodes.flatMap((aiNode) =>
      [...aiNode.ous].map((targetId) => ({
        startNodeId: aiNode.id,
        endNodeId: targetId,
        boundaryEventId:
          (aiNode.events || []).find((event) => event.ous.includes(targetId))?.id ?? null,
      }))
    )
  }

  static mapNewNodeAiIdToEndpointId(id: NodeDefinition['id']): string {
    return `ai-added-node-${id}`
  }

  static mapNewBoundaryEventAiIdToEndpointId(id: BoundaryEvent['id']): string {
    return `ai-added-boundary-event-${id}`
  }

  static traverseProcessBreadthFirst(nodes: NodeDefinition[]): Array<{
    level: number
    nodes: NodeDefinition[]
    sequences: any[]
  }> {
    const nodesMap = new Map(nodes.map((n) => [n.id, n]))

    const startNodes = nodes.filter((n) => n.type === EventNodes.START_EVENT)

    if (startNodes.length === 0) {
      return []
    }

    const visited = new Set<number>()
    const queue: Array<{ node: NodeDefinition; level: number }> = []
    const result: Array<{ level: number; nodes: NodeDefinition[]; sequences: any[] }> = []

    startNodes.forEach((node) => {
      queue.push({ node, level: 0 })
      visited.add(node.id)
    })

    while (queue.length > 0) {
      const { node, level } = queue.shift()

      if (!result[level]) {
        result[level] = { level, nodes: [], sequences: [] }
      }

      result[level].nodes.push(node)

      const outgoingSequences = node.outgoingSequences || []

      outgoingSequences.forEach((sequence) => {
        if (sequence) {
          result[level].sequences.push(sequence)
        }

        const nextNode = nodesMap.get(sequence.endNodeId)
        if (nextNode && !visited.has(nextNode.id)) {
          visited.add(nextNode.id)
          queue.push({ node: nextNode, level: level + 1 })
        }
      })
    }

    return result
  }

  static async createAreasForAllUserTasks(
    allNodes: NodeDefinition[],
    newNodes: NodeDefinition[],
    areasStore: AreaStores
  ): Promise<void> {
    const traversal = AiAnalystUtils.traverseProcessBreadthFirst(allNodes)
    const areas: Area[] = []
    const nodesMap = new Map<number, NodeDefinition>()
    newNodes.forEach((node) => {
      nodesMap.set(node.id, node)
    })
    const aiGeneratedNodesIds = newNodes.map((node) => node.id)
    const existingAreas = areasStore.areas$.getState()

    const userTaskData = new Map<
      number,
      {
        areaId: number
        swimlaneDefinitionId: string
        newPageId: number | null
      }
    >()

    existingAreas.forEach((swimlaneData) => {
      areas.push(...swimlaneData.navigationTree)

      if (swimlaneData.userTasks) {
        swimlaneData.userTasks.forEach((userTaskArea) => {
          if (!aiGeneratedNodesIds.includes(userTaskArea.nodeId)) {
            return
          }

          userTaskData.set(userTaskArea.nodeId, {
            areaId: userTaskArea.id,
            swimlaneDefinitionId: nodesMap.get(userTaskArea.nodeId)?.swimlaneDefinitionId,
            newPageId: null,
          })
        })
      }
    })

    const newNodesInTraversalOrder: NodeDefinition[] = []
    traversal.forEach(({ nodes }) => {
      nodes.forEach((node) => {
        if (node.type === ActivityNode.USER_TASK && aiGeneratedNodesIds.includes(node.id)) {
          newNodesInTraversalOrder.push(node)
        }
      })
    })

    const userTaskAreaIdMap = await this.#createAreasForUserTasks(
      newNodesInTraversalOrder,
      areas,
      areasStore
    )

    if (userTaskAreaIdMap.size !== newNodesInTraversalOrder.length) {
      throw new Error('Not all new pages were created')
    }

    userTaskAreaIdMap.forEach((areaId, userTaskId) => {
      userTaskData.set(userTaskId, {
        ...userTaskData.get(userTaskId),
        newPageId: areaId,
      })
    })

    AiAnalystUtils.#assignUserTasksToAreas(userTaskData)
  }

  static async #createAreasForUserTasks(
    userTasks: NodeDefinition[],
    areas: Area[],
    areasStore: AreaStores
  ): Promise<Map<number, number>> {
    const userTaskAreaIdMap = new Map<number, number>()

    for (const userTask of userTasks) {
      const newAreaId = await AiAnalystUtils.#createAreaForUserTask(
        userTask,
        NavigationType.PAGE,
        areas,
        areasStore
      )

      const newArea = areasStore.areas$
        .getState()
        .get(userTask.swimlaneDefinitionId)
        ?.navigationTree.find((area) => area.id === newAreaId)

      if (newArea) {
        areas.push(newArea)
      }

      userTaskAreaIdMap.set(userTask.id, newAreaId)
    }

    return userTaskAreaIdMap
  }

  static async #createAreaForUserTask(
    userTask: NodeDefinition,
    type: NavigationType,
    existingAreas: Area[],
    areasStore: AreaStores
  ): Promise<number> {
    return new Promise((resolve) => {
      const unsubscribe = areasStore.areasEffects.createAreaFx.doneData.watch(
        (createdArea: Area) => {
          if (createdArea.swimlaneDefinitionId === userTask.swimlaneDefinitionId) {
            unsubscribe()
            resolve(createdArea.id)
          }
        }
      )

      AreasEvents.createArea({
        type,
        label: `${_capitalize(type)} ${Area.getMaxAreaIndex(existingAreas, type) + 1}`,
        swimlaneDefinitionId: userTask.swimlaneDefinitionId,
        parentNavAreaId: null,
        uiTemplate: configDefaults()[type],
      })
    })
  }

  static #assignUserTasksToAreas(
    userTasksData: Map<
      number,
      {
        areaId: number
        swimlaneDefinitionId: string
        newPageId: number | null
      }
    >
  ): void {
    userTasksData.forEach((userTaskData) => {
      const { areaId, newPageId, swimlaneDefinitionId } = userTaskData

      AreasEvents.reorderAreas({
        swimlaneDefinitionId,
        areaId,
        toIndex: 0,
        fromIndex: 0,
        newParentId: newPageId,
        oldParentId: null,
        previousSiblingId: null,
        nextSiblingId: null,
      })
    })
  }

  static #createLayoutedMsaglGraph(
    nodes: { id: NodeDefinition['id']; type: NodeDefinition['type'] }[],
    sequences: {
      startNodeId: NodeDefinition['id']
      endNodeId: NodeDefinition['id']
    }[]
  ): MsaglGraph {
    const graph = new Graph()
    const geomGraph = new GeomGraph(graph)
    geomGraph.layoutSettings = new SugiyamaLayoutSettings()
    // @ts-expect-error - the layoutSettings types are not inferred correctly, but this works
    geomGraph.layoutSettings.layerDirection = LayerDirectionEnum.LR
    // @ts-expect-error - the layoutSettings types are not inferred correctly, but this works
    geomGraph.layoutSettings.yLayerSep = 100
    // @ts-expect-error - the layoutSettings types are not inferred correctly, but this works
    geomGraph.layoutSettings.commonSettings.nodeSeparation = 100
    geomGraph.layoutSettings.commonSettings.edgeRoutingSettings.EdgeRoutingMode =
      EdgeRoutingMode.Rectilinear

    const msNodes = new Map<string, Node>()
    const msEdges = new Map<string, Edge>()
    const geometryEdges = new Map<string, GeomEdge>()
    const geometryNodes = new Map<string, GeomNode>()

    nodes.forEach((node) => {
      const id = node.id.toString()
      const msNode = new Node(id)
      msNodes.set(id, msNode)
      graph.addNode(msNode)

      const geomNode = new GeomNode(msNode)
      geometryNodes.set(msNode.id, geomNode)
      const dimensions = getNodeDimensionsByType(node.type)
      geomNode.boundaryCurve = CurveFactory.createRectangle(
        dimensions.width,
        dimensions.height,
        new MsPoint(0, 0)
      )
    })

    sequences.forEach((sequence) => {
      const startNode = graph.findNode(sequence.startNodeId.toString())
      const endNode = graph.findNode(sequence.endNodeId.toString())
      const msEdge = new Edge(startNode, endNode)

      const edgeId = `${startNode.id}->${endNode.id}`
      msEdges.set(edgeId, msEdge)

      const geomEdge = new GeomEdge(msEdge)
      geometryEdges.set(edgeId, geomEdge)
    })

    layoutGeomGraph(geomGraph)

    return {
      graph,
      geomGraph,
      msNodes,
      msEdges,
      geometryNodes,
      geometryEdges,
    }
  }

  static #extractNodesPositionsFromMsaglGraph(
    msaglGraph: MsaglGraph
  ): Map<NodeDefinition['id'], NodeDefinition['layoutOptions']> {
    return Array.from(msaglGraph.msNodes.values()).reduce((positions, msNode) => {
      const geomNode = msaglGraph.geometryNodes.get(msNode.id)
      const id = parseInt(msNode.id, 10)
      const position = {
        x: geomNode.center.x - geomNode.width / 2,
        y: geomNode.center.y - geomNode.height / 2,
      }
      return produce(positions, (draft) => {
        draft.set(id, position)
      })
    }, new Map<number, NodeDefinition['layoutOptions']>())
  }

  static #extractSequencesPositionsFromMsaglGraph(
    msaglGraph: MsaglGraph
  ): Map<string, SequencePoints['layoutOptions']> {
    return Array.from(msaglGraph.msEdges.keys()).reduce((positions, id) => {
      const geomEdge = msaglGraph.geometryEdges.get(id)
      const segments = geomEdge.getSmoothPolyPoints()

      let points = []
      for (const point of segments) {
        points = [...points, point]
      }
      const pointsFromNodeEdges = points.slice(1, points.length - 1)

      let lines = []
      for (let i = 0; i < pointsFromNodeEdges.length - 1; i += 2) {
        const p1 = pointsFromNodeEdges[i]
        const p2 = pointsFromNodeEdges[i + 1]
        lines = [...lines, { p1, p2 }]
      }

      const linesWithStraightCorners = lines.map((line, index) => {
        const nextLine = lines[index + 1]
        const prevLine = lines[index - 1]

        const reversedLine = {
          p1: line.p2,
          p2: line.p1,
        }

        const intersectionWithNextLine = nextLine
          ? BpmnGraphUtil.lineIntersectionWithLine(line, nextLine)
          : null
        const intersectionWithPrevLine = prevLine
          ? BpmnGraphUtil.lineIntersectionWithLine(reversedLine, prevLine)
          : null

        return {
          p1: intersectionWithPrevLine ?? line.p1,
          p2: intersectionWithNextLine ?? line.p2,
        }
      })

      const straightCornersPoints =
        SequenceEntity.mapConnectedLinesToPoints(linesWithStraightCorners)

      return produce(positions, (draft) => {
        // TODO: See how to fix WriableDraft typing problem
        draft.set(id, straightCornersPoints as unknown as any)
      })
    }, new Map<string, SequencePoints['layoutOptions']>())
  }

  static #calculateNodesBoundingBoxesBySwimlane(
    nodesGroupedBySwimlane: Map<SwimlaneSummary['id'], AiNode['id'][]>,
    nodesMap: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>,
    nodes: RenderableNode[]
  ): Map<SwimlaneSummary['id'], Rectangle> {
    return Array.from(nodesGroupedBySwimlane.keys()).reduce((acc, swimlaneId) => {
      const nodesWithTypes = nodesGroupedBySwimlane.get(swimlaneId).map((nodeId) => ({
        layoutOptions: nodesMap.get(nodeId),
        type: nodes.find((node) => node.id === nodeId).type,
      }))

      const boundingBox = BpmnGraphUtil.nodesBoundingBox(nodesWithTypes)
      return produce(acc, (draft) => {
        draft.set(swimlaneId, boundingBox)
      })
    }, new Map<SwimlaneSummary['id'], Rectangle>())
  }

  static #calculateNodesYCoordRelativeToTheirParentSwimlane(
    nodesGroupedBySwimlane: Map<SwimlaneSummary['id'], AiNode['id'][]>,
    nodesMap: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>,
    boundingBoxes: Map<SwimlaneSummary['id'], Rectangle>
  ): Map<NodeDefinition['id'], NodeDefinition['layoutOptions']> {
    return Array.from(nodesGroupedBySwimlane.entries()).reduce((acc, [swimlaneId, nodeIds]) => {
      const swimlaneBoundingBox = boundingBoxes.get(swimlaneId)

      const nodesPositions = nodeIds.map((nodeId) => nodesMap.get(nodeId))
      const normalizedNodesPositions = nodesPositions.map((nodePosition) => ({
        x: nodePosition.x,
        y: nodePosition.y - swimlaneBoundingBox.tl.y,
      }))

      return produce(acc, (draft) => {
        nodeIds.forEach((nodeId, index) => {
          draft.set(nodeId, normalizedNodesPositions[index])
        })
      })
    }, new Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>())
  }

  static #calculateUpdatedSequencesPositionsForNormalizedNodesPositions(
    sequences: {
      startNodeId: NodeDefinition['id']
      endNodeId: NodeDefinition['id']
    }[],
    nodesYCoordsRelativeToSwimlanes: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>,
    sequencesMap: Map<string, SequencePoints['layoutOptions']>,
    nodesMap: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>
  ): Map<string, SequencePoints['layoutOptions']> {
    return sequences.reduce((acc, sequence) => {
      const layoutOptions = sequencesMap.get(`${sequence.startNodeId}->${sequence.endNodeId}`)

      const startNodePosition = nodesMap.get(sequence.startNodeId)
      const startNodeNormalizedPosition = nodesYCoordsRelativeToSwimlanes.get(sequence.startNodeId)

      const deltaY = startNodeNormalizedPosition.y - startNodePosition.y

      const newLayoutOptions = layoutOptions.map((point) => ({
        x: point.x,
        y: point.y + deltaY,
      }))

      return produce(acc, (draft) => {
        // TODO: See how to fix WriableDraft typing problem
        draft.set(
          `${sequence.startNodeId}->${sequence.endNodeId}`,
          newLayoutOptions as unknown as any
        )
      })
    }, new Map<string, SequencePoints['layoutOptions']>())
  }

  static #initializeSwimlanesLayout(
    boundingBoxes: Map<SwimlaneSummary['id'], Rectangle>,
    padding: Point
  ): Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']> {
    const maxWidth = Math.max(...Array.from(boundingBoxes.values()).map((box) => box.tr.x))

    return Array.from(boundingBoxes.entries()).reduce((acc, [swimlaneId, box]) => {
      const height = Math.max(
        Math.abs(box.tr.y - box.br.y) + padding.y,
        SWIMLANE_DEFAULT_LAYOUT_OPTIONS.height
      )
      const width = Math.max(maxWidth + padding.x, SWIMLANE_DEFAULT_LAYOUT_OPTIONS.width)

      const swimlane = {
        x: 0,
        y: 0,
        width,
        height,
      }

      return produce(acc, (draft) => {
        draft.set(swimlaneId, swimlane)
      })
    }, new Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>())
  }

  static #calculateSwimlanesYDelta(
    swimlanesPositions: Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>
  ): Map<SwimlaneSummary['id'], number> {
    const swimlanesDeltaY: Map<SwimlaneSummary['id'], number> = new Map()

    const swimlanesArray = Array.from(swimlanesPositions.entries()).map(
      ([swimlaneId, swimlane]) => ({
        id: swimlaneId,
        swimlane,
      })
    )

    swimlanesArray.forEach((swimlane, index) => {
      if (index === 0) {
        swimlanesDeltaY.set(swimlane.id, 0)
        return
      }

      const previousSwimlane = swimlanesArray[index - 1]
      const previousSwimlaneDeltaY = swimlanesDeltaY.get(previousSwimlane.id)
      const deltaY = previousSwimlane.swimlane.height + previousSwimlaneDeltaY
      swimlanesDeltaY.set(swimlane.id, deltaY)
    })

    return swimlanesDeltaY
  }

  static #moveSwimlanesWithDeltaY(
    swimlanesPositions: Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>,
    swimlanesDeltaY: Map<SwimlaneSummary['id'], number>
  ): Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']> {
    return Array.from(swimlanesPositions.entries()).reduce((acc, [swimlaneId, swimlane]) => {
      const deltaY = swimlanesDeltaY.get(swimlaneId)
      return produce(acc, (draft) => {
        draft.set(swimlaneId, {
          ...swimlane,
          y: deltaY,
        })
      })
    }, new Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>())
  }

  static #moveNodesForSwimlanePadding(
    nodesYCoordsRelativeToSwimlanes: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>,
    padding: Point
  ): Map<NodeDefinition['id'], NodeDefinition['layoutOptions']> {
    return Array.from(nodesYCoordsRelativeToSwimlanes.entries()).reduce(
      (acc, [nodeId, node]) =>
        produce(acc, (draft) => {
          draft.set(nodeId, {
            x: node.x + padding.x / 2,
            y: node.y + padding.y / 2,
          })
        }),
      new Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>()
    )
  }

  static #fitSequencesToSwimlanesMovement(
    nodes: RenderableNode[],
    padding: Point,
    updatedSequencesPositions: Map<string, SequencePoints['layoutOptions']>,
    swimlanesDeltaY: Map<SwimlaneSummary['id'], number>,
    swimlanesMovedMap: Map<SwimlaneSummary['id'], SwimlaneSummary['layoutOptions']>,
    nodesMapMovedByPadding: Map<NodeDefinition['id'], NodeDefinition['layoutOptions']>,
    sequences: {
      startNodeId: NodeDefinition['id']
      endNodeId: NodeDefinition['id']
    }[]
  ): Map<string, SequencePoints['layoutOptions']> {
    return sequences.reduce((acc, sequence) => {
      const layoutOptions = updatedSequencesPositions.get(
        `${sequence.startNodeId}->${sequence.endNodeId}`
      )

      const startNode = nodes.find((node) => node.id === sequence.startNodeId)
      const endNode = nodes.find((node) => node.id === sequence.endNodeId)

      const startNodeSwimlaneId = startNode.swimlaneDefinitionId
      const endNodeSwimlaneId = endNode.swimlaneDefinitionId

      if (startNodeSwimlaneId === endNodeSwimlaneId) {
        const newLayoutOptions = layoutOptions.map((point) => ({
          x: point.x + padding.x / 2,
          y: point.y + swimlanesDeltaY.get(startNodeSwimlaneId) + padding.y / 2,
        }))

        return produce(acc, (draft) => {
          // TODO: See how to fix WriableDraft typing problem
          draft.set(
            `${sequence.startNodeId}->${sequence.endNodeId}`,
            newLayoutOptions as unknown as any
          )
        })
      } else {
        const startSwimlane = {
          layoutOptions: swimlanesMovedMap.get(startNodeSwimlaneId),
        }

        const endSwimlane = {
          layoutOptions: swimlanesMovedMap.get(endNodeSwimlaneId),
        }

        const startNodeWithAbsolutePosition = produce(startNode, (draft) => {
          draft.layoutOptions = BpmnGraphUtil.getNodeAbsolutePosition(startSwimlane, {
            layoutOptions: nodesMapMovedByPadding.get(sequence.startNodeId),
          })
        })

        const endNodeWithAbsolutePosition = produce(endNode, (draft) => {
          draft.layoutOptions = BpmnGraphUtil.getNodeAbsolutePosition(endSwimlane, {
            layoutOptions: nodesMapMovedByPadding.get(sequence.endNodeId),
          })
        })

        return produce(acc, (draft) => {
          const newLayoutOptions = getSequencePointsBetweenNodes(
            startNodeWithAbsolutePosition,
            endNodeWithAbsolutePosition
          )
          draft.set(
            `${sequence.startNodeId}->${sequence.endNodeId}`,
            // TODO: See how to fix WriableDraft typing problem
            newLayoutOptions as unknown as any
          )
        })
      }
    }, new Map<string, SequencePoints['layoutOptions']>())
  }

  static #calculateBoundaryEventsPositions(
    nodes: RenderableNode[],
    boundaries: BoundaryEventRender[]
  ): Map<BoundaryEvent['id'], BoundaryEvent['layoutOptions']> {
    const nodesMap = new Map(nodes.map((node) => [node.id, node]))
    const boundariesByNodeMap = new Map(
      boundaries.reduce((acc, boundary) => {
        const nodeBoundaries = acc.get(boundary.nodeDefinitionId) ?? []
        return produce(acc, (draft) => {
          draft.set(boundary.nodeDefinitionId, [...nodeBoundaries, boundary])
        })
      }, new Map<NodeDefinition['id'], BoundaryEventRender[]>())
    )

    const positions = Array.from(boundariesByNodeMap.entries()).map(([nodeId, boundaries]) => {
      const node = nodesMap.get(nodeId)
      const nodeDimensions = getNodeDimensionsByType(node.type)
      const MaxBoundaryCountPerEdge = 4
      const spaceWidth = nodeDimensions.width / MaxBoundaryCountPerEdge
      return boundaries.map((boundary, index) => ({
        id: boundary.id,
        x:
          (MaxBoundaryCountPerEdge - Math.floor(index / 2) - 1) * spaceWidth +
          spaceWidth / 2 -
          BOUNDARY_EVENT_SIZE / 2,
        y: (index % 2 === 0 ? nodeDimensions.height : 0) - BOUNDARY_EVENT_SIZE / 2,
      }))
    })

    return new Map(
      positions.flatMap((boundaryPositions) =>
        boundaryPositions.map((position) => [position.id, position])
      )
    )
  }
}
